import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Popconfirm, Row, Skeleton, Table, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { DownloadOutlined, ExclamationCircleFilled, RetweetOutlined } from '@ant-design/icons';
import { Breakpoint } from 'antd/lib/_util/responsiveObserver';
import { createDeliveriesReport, getDeliveriesReport } from '../../api/deliveries';
import Page from '../../components/Page/Page';
import { routes } from '../../utils/routerHelper';
import styles from './PastDeliveriesPage.module.less';
import Button from '../../components/Button/Button';
import LoadMoreButton from '../../components/Button/LoadMoreButton';
import DownloadModal from '../../components/DownloadModal/DownloadModal';
import { whenCSVDownloadIsFailed, whenOrderIsFailedToRecreate, whenOrderIsRecreated } from './messages';
import { getErrorFromResponse } from '../../utils/apiHelpers';
import { DeliveryReportStatus } from '../../api/dtos/deliveries';
import { DELIVERY_REPORT_ID_KEY } from '../../utils/constants';
import { getItem, removeItem, setItem } from '../../utils/localStorage';
import Title from '../../components/Page/Title';
import useAnalytika from '../../utils/analytika/reporterAnalytika';
import {
  cnfCreateAgainClicked,
  completedDeliveriesPageOpen,
  deliveryDetailsClicked,
  downloadRecordsClicked
} from '../../utils/analytika/events';
import { ANALYTIKA_EVENTS_V2, ANALYTIKA_SCREENS } from '../../utils/analytika/enums';
import { Delivery } from '../../api/models/deliveries';
import CompletedDeliveryExpanded from '../../components/CompletedDeliveryExpanded/CompletedDeliveryExpanded';
import i18next from '../../i18next';
import StatusTag from '../../components/StatusTag';
import { DOWNLOAD_STATUS_FETCH_INTERVAL } from './PastDeliveriesPage';
import { AppDispatch } from '../../types/store';
import { deliveriesSelector } from '../../redux/selectors/deliveries';
import { loadingDeliveriesEffectsSelector } from '../../redux/selectors/loading';
import EmptyPage, { EMPTY_PAGE_USECASE } from '../pageNotFound/EmptyPage';
import { userSelector } from '../../redux/selectors/user';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import { colorBorder, colorTextSecondary } from '../../styles/theme';
import { formatDateTime } from '../../utils/componentHelpers';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { DeliveryOption } from '../../types';
import { FeatureFlag } from '../../utils/galileo/featureFlag.enum';

const getDeliveryIdentifier = (delivery: Delivery): string | number => delivery.id ?? delivery.bookingId;

const callCreateDeliveriesDeliveriesReport = ({ queryKey }: { queryKey: [string, [Date, Date]] }) => {
  const [, [startDate, endDate]] = queryKey;
  return createDeliveriesReport({ startDate, endDate });
};

const getDeliveriesReportById = ({ queryKey }: { queryKey: [string, string] }) => {
  const [, id] = queryKey;
  return getDeliveriesReport(id);
};

type UseDownloadReportProps = {
  downloadDates?: [Date, Date];
};

const useDownloadReport = (props: UseDownloadReportProps) => {
  const { downloadDates } = props;
  const deliveryReportIdFromLS = getItem(DELIVERY_REPORT_ID_KEY);
  const [shouldCheckReportStatus, setShouldCheckReportStatus] = useState(Boolean(deliveryReportIdFromLS));
  const {
    data: createDeliveriesReportData,
    refetch,
    isFetching: createDeliveriesReportLoading,
    error: createDeliveriesReportError
    //@ts-ignore
  } = useQuery(['createDeliveriesReport', downloadDates], callCreateDeliveriesDeliveriesReport, {
    enabled: downloadDates?.length === 2,
    retry: 0,
    cacheTime: 0
  });
  const {
    data: deliveriesReportData,
    isFetching: deliveriesReportLoading,
    error: deliveriesReportError
  } = useQuery(
    ['getDeliveriesReport', createDeliveriesReportData?.id || deliveryReportIdFromLS],
    //@ts-ignore
    getDeliveriesReportById,
    {
      enabled: Boolean(createDeliveriesReportData?.id || deliveryReportIdFromLS) && shouldCheckReportStatus,
      retry: 3,
      refetchInterval: DOWNLOAD_STATUS_FETCH_INTERVAL,
      cacheTime: 0
    }
  );

  useEffect(() => {
    if (createDeliveriesReportData) {
      setItem(DELIVERY_REPORT_ID_KEY, createDeliveriesReportData.id!);
    }
  }, [createDeliveriesReportData]);

  useEffect(() => {
    if (deliveriesReportData && deliveriesReportData.status !== DeliveryReportStatus.PROCESSING) {
      if (deliveriesReportData.status === DeliveryReportStatus.FAILED) {
        removeItem(DELIVERY_REPORT_ID_KEY);
      }
      setShouldCheckReportStatus(false);
    }
  }, [setShouldCheckReportStatus, deliveriesReportData]);

  useEffect(() => {
    if (deliveriesReportError) {
      setShouldCheckReportStatus(false);
      whenCSVDownloadIsFailed();
      removeItem(DELIVERY_REPORT_ID_KEY);
    }
  }, [deliveriesReportError]);
  useEffect(() => {
    if (createDeliveriesReportError) {
      whenCSVDownloadIsFailed();
      removeItem(DELIVERY_REPORT_ID_KEY);
    }
  }, [createDeliveriesReportError]);

  const recreateReport = useCallback(() => {
    if (downloadDates) {
      refetch().then(() => {
        setShouldCheckReportStatus(true);
      });
    }
  }, [refetch, setShouldCheckReportStatus, downloadDates]);
  return {
    downloadData: createDeliveriesReportData,
    shouldCheckReportStatus,
    setShouldCheckReportStatus,
    recreateReport,
    isDownloading: createDeliveriesReportLoading,
    downloadError: createDeliveriesReportError,
    deliveriesReportData,
    deliveriesReportLoading,
    deliveriesReportError
  };
};

const renderColumns = (recreateFunction: (bookingId: string, id: number | string) => Promise<void>) => [
  {
    title: i18next.t('pastDeliveriesPage.columns.deliveryId'),
    key: 'deliveryId',
    render: (_: any, delivery: Delivery) => getDeliveryIdentifier(delivery)
  },
  {
    title: i18next.t('pastDeliveriesPage.columns.createdAt'),
    dataIndex: 'pickUpTime',
    key: 'pickUpTime',
    width: 100,
    responsive: ['md'] as Breakpoint[],
    render: (_: any, { pickUpTime }: Delivery) => (pickUpTime ? formatDateTime(pickUpTime) : 'N/A')
  },
  {
    title: i18next.t('pastDeliveriesPage.columns.orderId'),
    dataIndex: 'orderId',
    key: 'orderId',
    responsive: ['sm'] as Breakpoint[],
    width: 120
  },
  {
    title: i18next.t('pastDeliveriesPage.columns.pickupAddress'),
    dataIndex: 'pickupAddressLine',
    key: 'pickupAddressLine',
    responsive: ['xl'] as Breakpoint[],
    render: (_: any, { pickupAddressLine }: Delivery) => pickupAddressLine
  },
  {
    title: i18next.t('pastDeliveriesPage.columns.customerName'),
    dataIndex: 'passenger',
    key: 'passenger',
    width: 180,
    responsive: ['xl'] as Breakpoint[],
    render: (_: any, { passenger }: Delivery) => (passenger ? `${passenger?.firstName} ${passenger?.lastName}` : '')
  },
  {
    title: i18next.t('pastDeliveriesPage.columns.completedAt'),
    dataIndex: 'completedAt',
    key: 'completedAt',
    width: 100,
    responsive: ['md'] as Breakpoint[],
    render: (_: any, { completedAt }: Delivery) => (completedAt ? formatDateTime(completedAt) : '-')
  },
  {
    title: i18next.t('pastDeliveriesPage.columns.status'),
    dataIndex: 'status',
    key: 'status',
    // eslint-disable-next-line react/display-name
    render: (_: any, { status, eta }: Delivery) => <StatusTag status={status} eta={eta} />
  },
  {
    title: '',
    dataIndex: 'bookingId',
    key: 'recreateBooking',
    width: 10,
    // eslint-disable-next-line react/display-name
    render: (_: any, { id, bookingId, bookingType }: Delivery) => {
      return bookingType !== DeliveryOption.SCHEDULED ? (
        <Popconfirm
          key={`${bookingId}-recreate`}
          title={i18next.t('pastDeliveriesPage.recreateConfirm.title')}
          description={i18next.t('pastDeliveriesPage.recreateConfirm.question')}
          okText={i18next.t('pastDeliveriesPage.recreateConfirm.confirm')}
          cancelText={i18next.t('pastDeliveriesPage.recreateConfirm.cancel')}
          onConfirm={async () => {
            await recreateFunction(bookingId, id);
          }}
          icon={<ExclamationCircleFilled style={{ color: colorTextSecondary }} />}
        >
          <Tooltip title={i18next.t('pastDeliveriesPage.recreateConfirm.hint')}>
            <RetweetOutlined size={14} />
          </Tooltip>
        </Popconfirm>
      ) : (
        ''
      );
    }
  }
];

export const CompletedDeliveriesPageV2 = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const urlPrams = useParams<{ deliveryId?: string }>();
  const selectedDeliveryId = urlPrams.deliveryId ? [urlPrams.deliveryId] : [];
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const { md: isMdBreakpoint } = useBreakpoint();

  const {
    deliveries: {
      fetchInitialCompletedDeliveries,
      fetchMoreCompletedDeliveries: fetchNextPage,
      fetchCompletedDeliveryDetails,
      recreateDelivery
    }
  } = useDispatch<AppDispatch>();

  const {
    completedDeliveries,
    completedDeliveriesPagination: { hasNextPage },
    isCompletedDeliveriesLoading
  } = useSelector(deliveriesSelector);
  const { fetchInitialCompletedDeliveries: isInitialListFetching } = useSelector(loadingDeliveriesEffectsSelector);

  const {
    merchantConfig: {
      accessTypes: { isPortalAccessEnabled }
    },
    userGalileoVars: { [FeatureFlag.IS_DOWNLOAD_RECORDS_ENABLED]: isDownloadRecordsEnabled }
  } = useSelector(userSelector);

  const [downloadDates, setDownloadDates] = useState<[Date, Date]>();
  const { setShouldCheckReportStatus, recreateReport, isDownloading, deliveriesReportData, deliveriesReportLoading } =
    useDownloadReport({
      downloadDates
    });

  const { fireEvent } = useAnalytika();

  const deliveries = completedDeliveries;

  useEffect(() => {
    const startFetching = async () => {
      const initDeliveries = await fetchInitialCompletedDeliveries();
      fireEvent(completedDeliveriesPageOpen({ completed_deliveries_list: initDeliveries }));
      if (selectedDeliveryId.length) {
        const booking = initDeliveries.find(
          d => String(d.id) === selectedDeliveryId[0] || d.bookingId === selectedDeliveryId[0]
        );
        booking && booking.bookingId && (await fetchCompletedDeliveryDetails(booking.bookingId));
      }
    };
    startFetching();

    return;
  }, []);

  const handleExpandClickTable = useCallback(
    async (expanded: boolean, deliverySelected: Delivery) => {
      if (expanded) {
        fireEvent(
          deliveryDetailsClicked(ANALYTIKA_SCREENS.MARKETPLACE_COMPLETED_DELIVERIES, {
            delivery_details: deliverySelected
          })
        );
        publishAV2Event({
          event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_ORDER_DETAILS,
          event_version: 2,
          order_details: JSON.stringify(deliverySelected)
        });
        history.push(
          routes.pastDeliveriesWithParam.replace(':deliveryId', String(getDeliveryIdentifier(deliverySelected)))
        );
        await fetchCompletedDeliveryDetails(deliverySelected.bookingId);
      } else {
        history.push(routes.pastDeliveries);
      }
    },
    [history, deliveries]
  );

  const handleDownloadClick = useCallback(() => {
    fireEvent(downloadRecordsClicked());
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_DOWNLOADRECORDS,
      event_version: 2
    });
    setShowDownloadModal(true);
  }, [setShowDownloadModal]);

  const onRecreate = useCallback((bookingId: string, id: number | string) => {
    fireEvent(cnfCreateAgainClicked(ANALYTIKA_SCREENS.MARKETPLACE_COMPLETED_DELIVERIES, { id, bookingId }));
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_CNF_CREATEAGAIN,
      event_version: 2,
      booking_id: id,
      booking_uid: bookingId
    });
    return recreateDelivery(bookingId)
      .then(() => {
        whenOrderIsRecreated();
      })
      .catch(error => {
        whenOrderIsFailedToRecreate(getErrorFromResponse(error));
      });
  }, []);

  const columns = useMemo(() => renderColumns(onRecreate), [onRecreate]);

  const onDownloadModalClose = useCallback(() => {
    setShowDownloadModal(false);
  }, [setShowDownloadModal]);

  const onDateChange = useCallback(
    (startDate: Date, endDate: Date) => {
      removeItem(DELIVERY_REPORT_ID_KEY);
      setDownloadDates([startDate, endDate]);
      setShouldCheckReportStatus(true);
      onDownloadModalClose();
    },
    [setDownloadDates, onDownloadModalClose, setShouldCheckReportStatus]
  );

  const isListEmpty = !isInitialListFetching && !deliveries.length;

  return (
    <Page>
      {isMdBreakpoint && (
        <div className={styles.pageTitle}>
          <Title text={t('pastDeliveriesPage.title')} />
          {isDownloadRecordsEnabled && (
            <div className={styles.titleButtonContainer}>
              {deliveriesReportData && deliveriesReportData.status === DeliveryReportStatus.READY && (
                <Button
                  disabled={isListEmpty}
                  size={'large'}
                  type="link"
                  href={deliveriesReportData.url}
                  download={'test122221.csv'}
                >
                  {t('pastDeliveriesPage.downloadStatus.ready')}
                </Button>
              )}
              {deliveriesReportData && deliveriesReportData.status === DeliveryReportStatus.FAILED && (
                <>
                  <Typography.Text>{t('pastDeliveriesPage.downloadStatus.failed')}</Typography.Text>
                  <Button
                    size={'large'}
                    disabled={isListEmpty}
                    className={styles.tryAgainBtn}
                    type="link"
                    onClick={recreateReport}
                  >
                    {t('general.tryAgain')}
                  </Button>
                </>
              )}
              <Button
                data-testid="downloadCSVButton"
                icon={<DownloadOutlined />}
                size={'large'}
                disabled={isListEmpty}
                onClick={handleDownloadClick}
                loading={
                  isDownloading ||
                  deliveriesReportData?.status === DeliveryReportStatus.PROCESSING ||
                  deliveriesReportLoading
                }
              >
                {t('pastDeliveriesPage.download')}
              </Button>
            </div>
          )}
        </div>
      )}

      {isInitialListFetching ? (
        <>
          <Skeleton active paragraph={{ rows: 8 }} />
        </>
      ) : (
        <>
          {isListEmpty ? (
            <EmptyPage
              useCase={EMPTY_PAGE_USECASE.COMPLETED_EMPTY}
              hasCTA={isPortalAccessEnabled}
              ctaAction={() => {
                history.push(routes.deliveriesDetailed);
              }}
            />
          ) : (
            <>
              <Table
                style={{ border: `1px solid ${colorBorder}`, borderRadius: 8, padding: 16 }}
                rowKey={(record: Delivery) => String(getDeliveryIdentifier(record))}
                columns={columns}
                dataSource={deliveries}
                pagination={false}
                expandable={{
                  expandedRowKeys: selectedDeliveryId,
                  defaultExpandAllRows: false,
                  onExpand: handleExpandClickTable,
                  expandRowByClick: true,
                  // eslint-disable-next-line react/display-name
                  expandedRowRender: (delivery: Delivery, index, indent, isExpanded) =>
                    isExpanded ? (
                      <CompletedDeliveryExpanded
                        recreateDelivery={onRecreate}
                        isRecreationInProgress={false}
                        delivery={delivery}
                      />
                    ) : (
                      ''
                    )
                }}
              />
              {hasNextPage && (
                <Row style={{ justifyContent: 'space-around', paddingTop: 20 }}>
                  <Col>
                    <LoadMoreButton size={'large'} onClick={fetchNextPage} loading={isCompletedDeliveriesLoading} />
                  </Col>
                </Row>
              )}
            </>
          )}
        </>
      )}
      {showDownloadModal && (
        <DownloadModal onClose={onDownloadModalClose} visible={showDownloadModal} onSubmit={onDateChange} />
      )}
    </Page>
  );
};

export default CompletedDeliveriesPageV2;
